import { useMemo } from 'react'
import { evaluateCards, transformMoneyIntegerToFloat } from '../../../../utils'
import type { PlayerResultsType, PokerRoomPropsType } from '../types'

export const usePokerRoomLogic = (
  props: Pick<
    PokerRoomPropsType,
    | 'table'
    | 'players'
    | 'userSeat'
    | 'pots'
    | 'tableInfo'
    | 'userId'
    | 'winners'
  >,
) => {
  const playersOffset = useMemo(() => {
    if (props.userSeat === null) return undefined
    // 7 is a number of a seat what will be overlayed by user's player component
    const bottomLeftSeat =
      (Math.min(7, props.tableInfo.maxPlayers) + props.tableInfo.maxPlayers) %
      props.tableInfo.maxPlayers
    return bottomLeftSeat - props.userSeat
  }, [props.userSeat, props.tableInfo.maxPlayers])

  const turnTimeout = useMemo(() => {
    if (!props.table.turnStartedAt) return null
    const timeDiff = Date.now() - new Date(props.table.turnStartedAt).valueOf()
    const timeDiffSeconds = Math.floor(timeDiff / 1000)
    return timeDiffSeconds > props.tableInfo.turnSeconds
      ? null
      : props.tableInfo.turnSeconds - timeDiffSeconds
  }, [props.table.turnStartedAt, props.tableInfo.turnSeconds])

  const playerResults = useMemo(() => {
    const results: PlayerResultsType = {}

    for (const winner of props.winners) {
      const userId = winner.id
      const board = winner.boardNumber
      const handScore = winner.handScore
      const handName = winner.handName

      if (!results[userId]) {
        results[userId] = {
          isWinner: true,
          winAmount: 0,
          hands: {},
        }
      }

      results[userId].winAmount =
        results[userId].winAmount + Number(winner.amount)

      if (board && handName && handScore) {
        results[userId].hands[board] = { handName, handScore }
      }

      if (handName && handScore) {
        const currentStrongest = results[userId].hands.strongest
        if (
          !currentStrongest ||
          (typeof currentStrongest.handScore === 'number' &&
            handScore < currentStrongest.handScore)
        ) {
          results[userId].hands.strongest = { handName, handScore }
        }
      }
    }

    for (const userId in results) {
      results[userId].winAmount = transformMoneyIntegerToFloat(
        results[userId].winAmount,
        props.tableInfo.currency,
      )
    }

    return results
  }, [props.winners])

  const userSecondHandName = useMemo(() => {
    const secondBoard = props.table.secondBoard
    if (!props.userId || !secondBoard) {
      return null
    }
    if (playerResults[props.userId]) {
      return playerResults[props.userId].hands.second?.handName || null
    }
    const userPlayer = props.players.find(
      (player) => player.userId === props.userId,
    )
    if (!userPlayer || !userPlayer.cards) {
      return null
    }
    const secondEvaluatedHand = evaluateCards(userPlayer.cards, secondBoard)
    return secondEvaluatedHand.handName
  }, [playerResults, props.table.handNumber])

  return {
    turnTimeout,
    playersOffset,
    playerResults,
    userSecondHandName,
  }
}
